import React, { useEffect, useMemo, useState } from "react";
import Layout from "../../components/Layout";
import {
  User,
  ClipboardList,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  CreditCard,
  KeyRound,
  Users,
} from "lucide-react";
import "./../../styles/member/dashboardadmin.css";
import { PostsService, type Post } from "../../services/postsService";
import { formatDateTime } from "../../utils/dateFormatter";
import { UsersService } from "../../services/usersService";
import { LogsService, type LogItem } from "../../services/logsService";
import { TransactionsService, type TransactionDto } from "../../services/transactionsService";

const Dashboardadmin: React.FC = () => {
  const [stats, setStats] = useState<{ total: number; pending: number; approved: number; rejected: number; deleted: number } | null>(null);
  const [approvedPosts, setApprovedPosts] = useState<Post[]>([]);
  const [recentLogs, setRecentLogs] = useState<LogItem[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [activeUsersPercent, setActiveUsersPercent] = useState<number>(0);
  const [revenueTotal, setRevenueTotal] = useState<number>(0);
  const [paidUsersCount, setPaidUsersCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchAll = async () => {
      setError(null);
      try {
        const [adminStats, approvedListResp, users, logsResp, txResp, pendingMeta] = await Promise.all([
          PostsService.getAdminStats(),
          PostsService.getApprovedPosts({ page: 1, limit: 20 }),
          UsersService.listUsers(),
          LogsService.getAdminLogs({ page: 1, limit: 100 }),
          TransactionsService.listAll({ page: 1, limit: 1, status: 'completed' }),
          PostsService.getPendingPosts({ page: 1, limit: 1 }),
        ]);

        // Lấy bài đã duyệt mới nhất
        const approvedLatestResp = await PostsService.getApprovedPosts({ page: 1, limit: 1 });
        const latestApprovedPost: Post | undefined = (approvedLatestResp.posts || [])[0];

        // Lấy bài bị từ chối mới nhất
        const rejectedLatestResp = await PostsService.getRejectedPosts({ page: 1, limit: 1 });
        const latestRejectedPost: Post | undefined = (rejectedLatestResp.posts || [])[0];

        // Lấy bài chờ duyệt mới nhất
        let latestPendingPost: Post | undefined = (pendingMeta.posts || [])[0];
        if (pendingMeta.totalPages && pendingMeta.totalPages > 1) {
          const pendingLast = await PostsService.getPendingPosts({ page: pendingMeta.totalPages, limit: 1 });
          latestPendingPost = pendingLast.posts?.[0] || latestPendingPost;
        }

        // Tính doanh thu tháng này - chỉ giao dịch hoàn thành
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const fromDate = startOfMonth.toISOString().split('T')[0];
        // Toàn bộ tháng: kết thúc là ngày 1 tháng kế tiếp (loại trừ)
        const firstOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        const toDate = firstOfNextMonth.toISOString().split('T')[0];

        let page = 1;
        const limit = 100;
        let total = 0;
        const payerSet = new Set<string>();
        while (true) {
          const pageResp = await TransactionsService.listAll({ page, limit, status: 'completed', type: 'payment', fromDate, toDate });
          for (const t of pageResp.data) {
            total += Number(t.amount) || 0;
            const uid = t.userId != null ? String(t.userId) : '';
            if (uid) payerSet.add(uid);
          }
          if (page * pageResp.limit >= pageResp.total) break;
          page += 1;
        }

        if (!isMounted) return;
        setStats(adminStats);
        setApprovedPosts(approvedListResp.posts || []);
        setTotalUsers(users.length || 0);
        setRevenueTotal(total);
        setPaidUsersCount(payerSet.size);
        // Tính % người dùng đang hoạt động
        const activeCount = users.filter((u: any) => u.isActive).length;
        const percentActive = users.length > 0 ? Math.round((activeCount / users.length) * 100) : 0;
        setActiveUsersPercent(percentActive);

        const latestCompletedTx: TransactionDto | undefined = (txResp.data || [])[0];
        const txLog: LogItem | undefined = latestCompletedTx
          ? ({
              id: -900001,
              activityType: 'TRANSACTION_COMPLETE',
              description: `Thanh toán thành công: ${new Intl.NumberFormat('vi-VN').format(latestCompletedTx.amount)} VND`,
              logLevel: 'INFO',
              result: 'SUCCESS',
              userId: latestCompletedTx.userId,
              createdAt: latestCompletedTx.createdAt,
            } as unknown as LogItem)
          : undefined;

        const pendingLog: LogItem | undefined = latestPendingPost
          ? ({
              id: -900002,
              activityType: 'POST_CREATE',
              description: latestPendingPost.title,
              logLevel: 'INFO',
              result: 'PENDING',
              userId: latestPendingPost.authorId,
              createdAt: latestPendingPost.createdAt,
            } as unknown as LogItem)
          : undefined;

        const approvedLog: LogItem | undefined = latestApprovedPost
          ? ({
              id: -900003,
              activityType: 'POST_PUBLISH',
              description: latestApprovedPost.title,
              logLevel: 'SUCCESS',
              result: 'SUCCESS',
              userId: latestApprovedPost.authorId,
              createdAt: latestApprovedPost.approvedAt || latestApprovedPost.createdAt,
            } as unknown as LogItem)
          : undefined;

        const rejectedLog: LogItem | undefined = latestRejectedPost
          ? ({
              id: -900004,
              activityType: 'POST_UPDATE',
              description: latestRejectedPost.title,
              logLevel: 'WARNING',
              result: 'FAILURE',
              userId: latestRejectedPost.authorId,
              createdAt: latestRejectedPost.approvedAt || latestRejectedPost.createdAt,
            } as unknown as LogItem)
          : undefined;

        const allLogsLatest = logsResp.data || [];
        const pickLatest = (types: string[]): LogItem | undefined => allLogsLatest.find(l => types.includes(l.activityType));
        const makePlaceholder = (type: string): LogItem => ({
          id: Math.floor(Math.random() * -1000000),
          activityType: type,
          description: undefined,
          logLevel: 'INFO',
          result: 'SUCCESS',
          createdAt: new Date().toISOString(),
        } as unknown as LogItem);
        const ordered: LogItem[] = [
          pickLatest(['REGISTER']) || makePlaceholder('REGISTER'),
          pendingLog || makePlaceholder('POST_CREATE'),
          txLog || makePlaceholder('TRANSACTION_COMPLETE'),
          approvedLog || makePlaceholder('POST_PUBLISH'),
          rejectedLog || makePlaceholder('POST_UPDATE'),
        ];
        setRecentLogs(ordered);
      } catch (e: any) {
        if (!isMounted) return;
        setError(e?.message || "Không thể tải dữ liệu dashboard admin");
      }
    };

    // Tải dữ liệu lần đầu
    setIsLoading(true);
    fetchAll().finally(() => isMounted && setIsLoading(false));

    // Tự cập nhật mỗi 30 giây
    const intervalId = window.setInterval(fetchAll, 30000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const aggregates = useMemo(() => {
    const totalViews = approvedPosts.reduce((sum, p) => sum + (typeof p.viewCount === "number" ? p.viewCount : 0), 0);
    const approveRate = stats && stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0;
    return { totalViews, approveRate };
  }, [approvedPosts, stats]);

  const renderLogIcon = (type?: string) => {
    switch (type) {
      case 'REGISTER':
        return <Users size={20} className="card-icon users" />;
      case 'TRANSACTION_COMPLETE':
        return <CreditCard size={20} className="card-icon credit" />;
      case 'POST_CREATE':
        return <ClipboardList size={20} className="card-icon clipboard" />;
      case 'POST_PUBLISH':
        return <CheckCircle size={20} className="card-icon check" />;
      case 'POST_UPDATE':
        return <XCircle size={20} className="card-icon x" />;
      default:
        return <CheckCircle size={20} className="card-icon check" />;
    }
  };

  const renderLogText = (log: LogItem) => {
    const email = log.user?.email || '';
    const isPlaceholder = typeof log.id === 'number' && log.id < 0 && log.id !== -900001 && log.id !== -900002 && log.id !== -900003 && log.id !== -900004;
    switch (log.activityType) {
      case 'REGISTER':
        return isPlaceholder ? 'Chưa có người dùng đăng ký mới' : `Người dùng mới đăng ký: ${email || 'Người dùng mới'}`;
      case 'POST_CREATE':
        return isPlaceholder ? 'Chưa có bài viết mới cần duyệt' : `Bài viết mới cần duyệt${log.description ? `: "${log.description}"` : ''}`;
      case 'TRANSACTION_COMPLETE':
        return isPlaceholder ? 'Chưa có thanh toán thành công' : (log.description || 'Thanh toán thành công');
      case 'POST_PUBLISH':
        return isPlaceholder ? 'Chưa có bài viết được duyệt' : `Đã duyệt bài viết${log.description ? `: "${log.description}"` : ''}`;
      case 'POST_UPDATE':
        return isPlaceholder ? 'Chưa có bài viết bị từ chối' : `Từ chối bài viết${log.description ? `: "${log.description}"` : ''}`;
      default:
        return log.description || log.activityType;
    }
  };

  const formatVND = (value: number) => new Intl.NumberFormat('vi-VN').format(value);

  return (
    <Layout>
      <div className="dashboard">
        {/* Hàng 1: Thống kê nhanh */}
        <div className="stats-row">
          <div className="card-stats">
            <div className="card-title">
              <p>Tổng người dùng</p>
              <User size={20} className="card-icon users" />
            </div>
            <div className="card-statis">
              <h3>{isLoading ? "..." : totalUsers.toLocaleString()}</h3>
              <span>{isLoading || !stats ? "" : `${stats.approved.toLocaleString()} đã duyệt (${(aggregates.approveRate || 0)}%)`}</span>
            </div>
          </div>
          <div className="card-stats">
            <div className="card-title">
              <p>Tổng bài viết</p>
              <ClipboardList size={20} className="card-icon posts" />
            </div>
            <div className="card-statis">
              <h3>{isLoading || !stats ? "..." : stats.total.toLocaleString()}</h3>
              <span>{isLoading || !stats ? "" : `${stats.approved.toLocaleString()} đã duyệt (${(aggregates.approveRate || 0)}%)`}</span>
            </div>
          </div>
          <div className="card-stats">
            <div className="card-title">
              <p>Chờ duyệt</p>
              <Clock size={20} className="card-icon pending" />
            </div>
            <div className="card-statis">
              <h3>{isLoading || !stats ? "..." : stats.pending.toLocaleString()}</h3>
              <span>Bài viết cần xem xét</span>
            </div>
          </div>
          <div className="card-stats">
            <div className="card-title">
              <p>Lượt xem</p>
              <Eye size={20} className="card-icon views" />
            </div>
            <div className="card-statis">
              <h3>{isLoading ? "..." : aggregates.totalViews.toLocaleString()}</h3>
              <span>Tổng lượt xem các bài đã duyệt</span>
            </div>
          </div>
        </div>

        {/* Hàng 2: Tình trạng hệ thống + Hoạt động gần đây */}
        <div className="system-activity-row">
          <div className="card large">
            <h4>Tình trạng hệ thống</h4>
            <p className="sub">Các chỉ số quan trọng của hệ thống</p>

            <div className="progress-group">
              <span>Tỉ lệ duyệt bài viết</span>
              <div className="progress">
                <div className="progress-bar" style={{ width: `${aggregates.approveRate}%` }}></div>
              </div>
              <span className="progress-label">{aggregates.approveRate}%</span>
            </div>

            <div className="progress-group">
              <span>Người dùng hoạt động</span>
              <div className="progress">
                <div className="progress-bar active" style={{ width: `${activeUsersPercent}%` }}></div>
              </div>
              <span className="progress-label">{activeUsersPercent}%</span>
            </div>

            <div className="status-numbers">
              <span className="approved">{stats ? stats.approved.toLocaleString() : "..."} Đã duyệt</span>
              <span className="rejected">{stats ? stats.rejected.toLocaleString() : "..."} Từ chối</span>
            </div>
          </div>

          <div className="card large">
            <h4>Hoạt động gần đây</h4>
            <p className="sub">Các sự kiện mới nhất trong hệ thống</p>

            <ul className="activity-list">
              {error ? (
                <li>{error}</li>
              ) : (
                recentLogs.map((log) => (
                  <li key={log.id}>
                    <div className="activity-cardd">
                      {renderLogIcon(log.activityType)}
                    </div>
                    <span className="activity-text">{renderLogText(log)}</span>
                    <span className="time">{(typeof log.id === 'number' && log.id < 0 && log.id !== -900001 && log.id !== -900002 && log.id !== -900003 && log.id !== -900004) ? '' : formatDateTime(log.createdAt)}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>

        {/* Hàng 3: Thao tác nhanh */}
        <div className="quick-actions">
          <div className="quick-actions-title">
            <h3>Tổng quan hệ thống</h3>
            <span>Theo dõi nhanh số lượng bài viết, người dùng và từ khóa đang hoạt động</span>
          </div>
          <div className="card-container">
            <div className="quick-actions-card">
              <div className="card-info-container">
                <Clock size={36} className="card-icon pending" />
                <div className="card-info">
                  <h5>Bài viết chờ duyệt</h5>
                  <span>{stats ? stats.pending : 0} bài viết</span>
                </div>
              </div>
              <div className="card-number">{stats ? stats.pending : 0}</div>
            </div>
            <div className="quick-actions-card">
              <div className="card-info-container">
                <Users size={36} className="card-icon user" />
                <div className="card-info">
                  <h5>Người dùng</h5>
                  <span>Tổng số hiện tại</span>
                </div>
              </div>
              <div className="card-number">{totalUsers}</div>
            </div>
            <div className="quick-actions-card">
              <div className="card-info-container">
                <KeyRound size={36} className="card-icon keywords" />
                <div className="card-info">
                  <h5>Từ khóa hiệu suất cao</h5>
                  <span>Top từ khóa mang lại traffic cao</span>
                </div>
              </div>
              <div className="card-number">5</div>
            </div>
          </div>
        </div>

        {/* Hàng 4: Doanh thu */}
        <div className="card revenue">
          <h4>Tóm tắt doanh thu</h4>
          <p>Doanh thu tháng này</p>
          <div className="revenue-amount">{formatVND(revenueTotal)} VND</div>
          <span>Từ {paidUsersCount} người dùng trả phí</span>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboardadmin;
